export const OWNER_JARVIS_CAPABILITY_CONTRACT_VERSION = 2;

export const OWNER_JARVIS_OPERATION_KIND = Object.freeze({
  READ: "read",
  PROPOSE: "propose",
  SAFE_WRITE: "safe_write",
  SENSITIVE_WRITE: "sensitive_write",
  // Aliases temporales para consumidores previos a MEJ-065.
  ACTION: "propose",
  WRITE: "safe_write",
});

export const OWNER_JARVIS_CONFIRMATION = Object.freeze({
  NEVER: "never",
  REQUIRED: "required",
  SURFACE: "surface",
});

const VALID_OPERATION_KINDS = new Set(Object.values(OWNER_JARVIS_OPERATION_KIND));
const VALID_CONFIRMATION_MODES = new Set(Object.values(OWNER_JARVIS_CONFIRMATION));

function cleanString(value) {
  return String(value || "").trim();
}

function normalizeErrors(errors = {}) {
  return {
    identify: cleanString(errors.identify),
    prepare: cleanString(errors.prepare),
    execute: cleanString(errors.execute),
    confirmation: cleanString(errors.confirmation),
    cancelled: cleanString(errors.cancelled),
    unsupported: cleanString(errors.unsupported),
  };
}

function normalizeVoiceMetadata(voice) {
  if (!voice) return null;
  const key = cleanString(voice.key);
  if (!key) throw new Error("Una operación de Jarvis con voice debe declarar voice.key.");
  return { ...voice, key };
}

function normalizeBackendMethods(methods = []) {
  return [...new Set((methods || []).map(cleanString).filter(Boolean))];
}

export function isOwnerJarvisWriteKind(kind) {
  return kind === OWNER_JARVIS_OPERATION_KIND.SAFE_WRITE || kind === OWNER_JARVIS_OPERATION_KIND.SENSITIVE_WRITE;
}

export function ownerJarvisOperationRequiresConfirmation(operation) {
  return operation?.kind === OWNER_JARVIS_OPERATION_KIND.SENSITIVE_WRITE
    || operation?.confirmation === OWNER_JARVIS_CONFIRMATION.REQUIRED;
}

export function defineOwnerJarvisOperation({
  id,
  kind = OWNER_JARVIS_OPERATION_KIND.READ,
  confirmation = null,
  prepare = null,
  cancel = null,
  execute,
  voice = null,
  invalidates = [],
  backendMethods = [],
  errors = {},
} = {}) {
  const operationId = cleanString(id);
  if (!operationId) throw new Error("Cada operación de Jarvis debe declarar id.");
  if (!VALID_OPERATION_KINDS.has(kind)) throw new Error(`Tipo de operación de Jarvis inválido: ${kind}`);
  if (prepare != null && typeof prepare !== "function") throw new Error(`La operación ${operationId}: prepare debe ser función.`);
  if (cancel != null && typeof cancel !== "function") throw new Error(`La operación ${operationId}: cancel debe ser función.`);
  if (typeof execute !== "function") throw new Error(`La operación ${operationId} debe declarar execute().`);

  const normalizedConfirmation = confirmation || (
    kind === OWNER_JARVIS_OPERATION_KIND.SENSITIVE_WRITE
      ? OWNER_JARVIS_CONFIRMATION.REQUIRED
      : OWNER_JARVIS_CONFIRMATION.NEVER
  );
  if (!VALID_CONFIRMATION_MODES.has(normalizedConfirmation)) {
    throw new Error(`Modo de confirmación de Jarvis inválido: ${normalizedConfirmation}`);
  }

  if (kind === OWNER_JARVIS_OPERATION_KIND.READ && normalizedConfirmation !== OWNER_JARVIS_CONFIRMATION.NEVER) {
    throw new Error(`La operación de lectura ${operationId} no puede exigir confirmación.`);
  }
  if (kind === OWNER_JARVIS_OPERATION_KIND.PROPOSE && normalizedConfirmation === OWNER_JARVIS_CONFIRMATION.REQUIRED) {
    throw new Error(`La propuesta ${operationId} no puede usar confirmación required: todavía no ejecuta una mutación.`);
  }
  if (kind === OWNER_JARVIS_OPERATION_KIND.SENSITIVE_WRITE && normalizedConfirmation !== OWNER_JARVIS_CONFIRMATION.REQUIRED) {
    throw new Error(`La operación sensible ${operationId} debe exigir confirmación explícita.`);
  }
  if (voice && normalizedConfirmation === OWNER_JARVIS_CONFIRMATION.REQUIRED) {
    throw new Error(`La operación ${operationId} no puede ser voice action directa y exigir confirmación en el mismo paso.`);
  }

  const normalizedInvalidates = [...new Set((invalidates || []).map(cleanString).filter(Boolean))];
  if (!isOwnerJarvisWriteKind(kind) && normalizedInvalidates.length) {
    throw new Error(`La operación ${operationId} no modifica estado y no puede invalidar snapshot.`);
  }

  return Object.freeze({
    id: operationId,
    kind,
    confirmation: normalizedConfirmation,
    prepare,
    cancel,
    execute,
    voice: normalizeVoiceMetadata(voice),
    invalidates: Object.freeze(normalizedInvalidates),
    backendMethods: Object.freeze(normalizeBackendMethods(backendMethods)),
    errors: normalizeErrors(errors),
  });
}

export function defineOwnerJarvisCapability({
  id,
  domain,
  description = "",
  reads = [],
  identify = null,
  operations = [],
  errors = {},
} = {}) {
  const capabilityId = cleanString(id);
  const capabilityDomain = cleanString(domain);
  if (!capabilityId) throw new Error("Cada capability de Jarvis debe declarar id.");
  if (!capabilityDomain) throw new Error(`Capability ${capabilityId}: falta domain.`);
  if (identify != null && typeof identify !== "function") throw new Error(`Capability ${capabilityId}: identify debe ser función.`);

  const operationIds = new Set();
  const normalizedOperations = (operations || []).map((operation) => {
    const normalized = operation?.execute ? defineOwnerJarvisOperation(operation) : operation;
    if (!normalized?.id || typeof normalized.execute !== "function") {
      throw new Error(`Capability ${capabilityId}: operación inválida.`);
    }
    if (operationIds.has(normalized.id)) throw new Error(`Capability ${capabilityId}: operación duplicada ${normalized.id}.`);
    operationIds.add(normalized.id);
    return normalized;
  });

  if (!normalizedOperations.length) throw new Error(`Capability ${capabilityId}: debe declarar al menos una operación permitida.`);
  const normalizedReads = [...new Set((reads || []).map(cleanString).filter(Boolean))];

  return Object.freeze({
    contractVersion: OWNER_JARVIS_CAPABILITY_CONTRACT_VERSION,
    id: capabilityId,
    domain: capabilityDomain,
    description: cleanString(description),
    reads: Object.freeze(normalizedReads),
    identify,
    operations: Object.freeze(normalizedOperations),
    errors: Object.freeze(normalizeErrors(errors)),
  });
}

export function isOwnerJarvisCapabilityContract(capability) {
  return capability?.contractVersion === OWNER_JARVIS_CAPABILITY_CONTRACT_VERSION
    && Boolean(capability.id)
    && Boolean(capability.domain)
    && Array.isArray(capability.operations);
}
